import { useState } from "react";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { useWindowWidth } from "../../hooks/useWindowWidth";
import { useThemeContext } from "../../theme/ThemeContext";
import { overlayBackdrop } from "../../theme/styles";
import { RADIUS } from "../../theme/tokens";
import { Button } from "../shared";
import { dbUpdateEntry } from "../../db";
import { appendRemark } from "../../utils/entryUtils";
import { getAirlineCode } from "../../utils/timeUtils";
import { useSwipeToClose } from "../../hooks/useSwipeToClose";

export function WillHoldModal({ entry, date, appDate, airline, partnerName, onClose, onSave, onSendMessage }) {
  const { SA, GLASS, tokens, TYPOGRAPHY } = useThemeContext();
  useLockBodyScroll();
  useEscapeKey(onClose);

  const isEdit =!!(entry.willHold && entry.holdUntil);
  const [time, setTime] = useState(isEdit ? entry.holdUntil : "");
  const [saving, setSaving] = useState(false);
  const winWidth = useWindowWidth();
  const isWide = winWidth >= 768;
  const { handleProps, dragStyle } = useSwipeToClose(onClose, { enabled: !isWide });

  // Accept either HH:MM (from type="time") or partial manual input
  const timeValid = /^\d{2}:\d{2}$/.test(time);

  const handleSave = async () => {
    if (!timeValid) return;
    setSaving(true);
    const remarkText = "WILL HOLD UNTIL " + time;
    const newRemark = appendRemark(entry.remark, remarkText, airline, partnerName);
    const updated = { ...entry, willHold: true, holdUntil: time, remark: newRemark };
    await dbUpdateEntry(updated, date || "");
    // Auto-message to inbound carrier
    const inAl = getAirlineCode(entry.inAirline, entry.inFlt);
    if (inAl && onSendMessage) {
      const who = partnerName ? airline + "/" + partnerName : airline;
      const msgBody = "[AUTO] " + who + ": Will hold " + (entry.outFlt || "outbound") +
        (entry.dest ? " to " + entry.dest : "") +
        " until " + time + " for " + (entry.inFlt || "inbound") + " passengers";
      try {
        await onSendMessage({
          from_airline: airline,
          to_airline: inAl,
          body: msgBody,
          urgent: false,
          read: false,
          date: appDate || date || new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" }),
          connection_id: entry.id || null,
          from_scco_name: partnerName || null,
        });
      } catch (e) { console.warn("Will hold auto-message failed:", e); }
    }
    onSave(updated);
    setSaving(false);
    onClose();
  };

  const handleClear = async () => {
    setSaving(true);
    const newRemark = appendRemark(entry.remark, "HOLD CANCELLED", airline, partnerName);
    const updated = { ...entry, willHold: false, holdUntil: "", remark: newRemark };
    await dbUpdateEntry(updated, date || "");
    // Auto-message to inbound carrier
    const inAl = getAirlineCode(entry.inAirline, entry.inFlt);
    if (inAl && onSendMessage) {
      const who = partnerName ? airline + "/" + partnerName : airline;
      const msgBody = "[AUTO] " + who + ": Hold cancelled for " + (entry.outFlt || "outbound") +
        (entry.dest ? " to " + entry.dest : "") +
        " — " + (entry.inFlt || "inbound") + " passengers no longer being held";
      try {
        await onSendMessage({
          from_airline: airline,
          to_airline: inAl,
          body: msgBody,
          urgent: false,
          read: false,
          date: appDate || date || new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" }),
          connection_id: entry.id || null,
          from_scco_name: partnerName || null,
        });
      } catch (e) { console.warn("Hold cancel auto-message failed:", e); }
    }
    onSave(updated);
    setSaving(false);
    onClose();
  };

  return (
    <div
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ ...overlayBackdrop, zIndex: 200,
        display: "flex", alignItems: isWide ? "center" : "flex-end",
        justifyContent: "center", padding: isWide ? 24 : 0 }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{ ...GLASS.modal,
          width: "100%", maxWidth: isWide ? 360 : "100%",
          borderTop: isWide ? undefined : `2px solid ${tokens.partner}`,
          borderRadius: isWide ? 16 : "16px 16px 0 0",
          maxHeight: "88vh", display: "flex", flexDirection: "column", ...dragStyle }}>

        {!isWide && (
          <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px",
                        flexShrink: 0, touchAction: "none", cursor: "grab" }} {...handleProps}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: SA.borderGray }} />
          </div>
        )}
        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${SA.borderGray}`,
          display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, letterSpacing: "0.18em",
              textTransform: "uppercase", marginBottom: 4 }}>
              {isEdit ? "Edit Hold Time" : "Will Hold"}
            </div>
            <div style={{ fontSize: TYPOGRAPHY.sub, color: SA.lightSilver, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
              {entry.inFlt} {"→"} {entry.outFlt}
              {entry.dest ? <span style={{ color: SA.silver }}> · {entry.dest}</span> : null}
            </div>
          </div>
          <Button variant="icon" onClick={onClose}
            style={{ background: SA.medGray, color: SA.silver, width: 28, height: 28, fontSize: 16, flexShrink: 0 }}>
            ×
          </Button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          <div style={{ background: tokens.partnerSurface, border: `1px solid ${tokens.partnerBorder}`,
            borderRadius: RADIUS.md, padding: "10px 14px", marginBottom: 16,
            fontSize: TYPOGRAPHY.body, color: tokens.partner, lineHeight: 1.5 }}>
            Enter the time you will hold this connection until. This will be visible to all users monitoring this card.
          </div>
          <div>
            <label style={{ display: "block", fontSize: TYPOGRAPHY.caption, color: SA.silver,
              letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>
              Hold Until (local time)
            </label>
            <input
              type="text"
              inputMode="numeric"
              placeholder="HH:MM"
              value={time}
              onChange={e => {
                const raw = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
                setTime(raw.length >= 3 ? raw.slice(0, 2) + ":" + raw.slice(2) : raw);
              }}
              autoFocus
              onKeyDown={e => e.key === "Enter" && timeValid && handleSave()}
              style={{ width: "100%", boxSizing: "border-box",
                background: SA.darkGray, border: `1px solid ${SA.borderGray}`,
                borderRadius: RADIUS.md, color: SA.white,
                padding: "14px 16px", fontSize: 22, fontWeight: 700,
                fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", outline: "none",
                letterSpacing: "0.06em", textAlign: "center" }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px 16px", borderTop: `1px solid ${SA.borderGray}` }}>
          {isEdit && (
            <Button variant="secondary" onClick={handleClear} disabled={saving}
              style={{ width: "100%", padding: "11px", fontSize: TYPOGRAPHY.caption,
                letterSpacing: "0.12em", textTransform: "uppercase",
                fontFamily: "'Helvetica Neue',Arial,sans-serif",
                borderRadius: RADIUS.md, marginBottom: 8,
                color: tokens.dangerMuted, borderColor: tokens.dangerBorderLo }}>
              Cancel Hold
            </Button>
          )}
          <div style={{ display: "flex", gap: 10 }}>
            <Button variant="secondary" onClick={onClose}
              style={{ flex: 1, padding: "13px", fontSize: TYPOGRAPHY.caption,
                letterSpacing: "0.12em", textTransform: "uppercase",
                fontFamily: "'Helvetica Neue',Arial,sans-serif",
                borderRadius: RADIUS.md }}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!timeValid || saving}
              style={{ flex: 2, padding: "13px",
                background: timeValid && !saving ? tokens.partnerSurface : SA.darkGray,
                border: timeValid && !saving ? `1px solid ${tokens.partnerBorder}` : `1px solid ${SA.borderGray}`,
                color: timeValid && !saving ? tokens.partner : SA.silver,
                fontSize: TYPOGRAPHY.body, letterSpacing: "0.1em", textTransform: "uppercase",
                fontFamily: "'Helvetica Neue',Arial,sans-serif", fontWeight: 700,
                borderRadius: RADIUS.md, transition: "all 0.2s" }}>
              {saving ? "Saving..." : isEdit ? "Update Hold" : "Set Hold"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
